import TopPanel from '@/components/ui/TopPanel';
import Sidebar from '@/components/ui/Sidebar';
import TableHeader from '@/components/ui/TableHeader';
import TripsTable from '@/components/trips/table/TripsTable';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { useUser } from '@/hooks/auth/useUser';
import { useTranslations } from '@/i18n';
import { useMemo, useState, useCallback } from 'react';
import { createTripFilters } from '@/config/filters';
import { useDebounce } from '@/hooks/useDebounce';
import { useApprovedTrips } from '@/hooks/trips/useTrips';

export function MyApprovalsPage() {
  const { data: user, isLoading } = useUser();
  const tFilters = useTranslations('filters');
  const tTrips = useTranslations('trips');

  const filterSections = useMemo(() => createTripFilters(tFilters), [tFilters]);

  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 400);
  const searchQuery = useMemo(() => debouncedSearch.trim(), [debouncedSearch]);

  const handleFiltersChange = useCallback((filters: Record<string, string[]>) => {
    setActiveFilters(filters);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Spinner variant="circle" size={32} className="text-gray-500" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="flex h-screen flex-col bg-white">
      <TopPanel />

      <div className="flex flex-1 overflow-hidden">
        <Sidebar />

        <main className="flex-1 overflow-y-auto bg-white p-4 md:p-8 md:pr-22">
          <div className="max-w-full">
            <div className="mb-4 md:mb-6">
              <TableHeader
                title={tTrips('approvalsTitle')}
                filterSections={filterSections}
                onFiltersChange={handleFiltersChange}
                onSearchChange={handleSearchChange}
                searchPlaceholder={tTrips('search')}
              />
            </div>

            <TripsTable
              filters={activeFilters}
              search={searchQuery}
              useTripsHook={useApprovedTrips}
              variant="approvals"
              currentUserRoles={user.roles ?? []}
            />
          </div>
        </main>
      </div>
    </div>
  );
}
